// The game-start lifecycle (plan 17): boot -> menu <-> difficulty ->
// starting (fade) -> running. A plain state machine over injected callbacks —
// no DOM, no WebGL imports — so its transitions are unit-testable; the
// composition root (`main.ts`) supplies the real session builders, the cube
// rotation and the fade overlay. Ports `Cube.HandleUserInputInMenu`
// (`Cube.cs:2648`): tap plays the item's `move` clip, and the action fires
// only when the clip finishes.

import { DIFFICULTIES, type DifficultyDef } from "../game/defs";

/**
 * The tappable items, port-side names. The original raycasts against mesh
 * names (`menuItems`, `Cube.cs:2570`); the shipped labels differ from these
 * internal names for the difficulty trio (NORMAL / BUGGY / BUGG HARD — see
 * `model-catalog.ts`).
 */
export type MenuItemName =
  | "start"
  | "howto"
  | "options"
  | "highscore"
  | "easy"
  | "normal"
  | "hard"
  | "back";

/**
 * A page is a set of items anchored to one cube face. `main` sits on the
 * resting front face; `difficulty` one clockwise quarter-turn away (+X), as
 * in the original where submenus are faces of the same rotation.
 */
export type MenuPage = "main" | "difficulty";

export const PAGE_ITEMS: Record<MenuPage, readonly MenuItemName[]> = {
  main: ["start", "howto", "options", "highscore"],
  difficulty: ["easy", "normal", "hard", "back"],
};

/**
 * How long a pressed item animates before its action fires — every menu
 * model's cooked `move` clip is 15 frames @ 30 fps, `(frames - 1) / fps`
 * like `DIE_SECONDS`. The original polls `Animation.isPlaying`
 * (`InputStates.MouseDown`); the clip length as a timer is the same wait.
 */
export const MENU_PRESS_SECONDS = 14 / 30;

/** The difficulty each item starts, or null for every non-starting item. */
export function itemDifficulty(item: MenuItemName): DifficultyDef | null {
  switch (item) {
    case "easy":
      return DIFFICULTIES[0];
    case "normal":
      return DIFFICULTIES[1];
    case "hard":
      return DIFFICULTIES[2];
    default:
      return null;
  }
}

export type ShellState =
  | {
      readonly kind: "menu";
      readonly page: MenuPage;
      /** The item mid-press-animation, or null. Input is locked while set. */
      readonly pressed: { readonly item: MenuItemName; elapsed: number } | null;
    }
  | { readonly kind: "rotating"; readonly toPage: MenuPage }
  | { readonly kind: "starting"; readonly difficulty: DifficultyDef }
  | { readonly kind: "running" };

/** What one run owns; the shell only ever needs to dispose the old one. */
export interface SessionLike {
  dispose(): void;
}

/**
 * The shell's hooks into the real world. `rotate`/`fadeOut` report completion
 * through their callback so the shell needs no timers of its own beyond the
 * press clock — tests drive transitions by invoking the captured callbacks.
 */
export interface ShellDeps {
  buildMenuSession(): SessionLike;
  buildGameSession(difficulty: DifficultyDef): SessionLike;
  /** Animate a quarter-turn about world Y; `dir` as `input.ts` `Step.dir`. */
  rotate(dir: 1 | -1, done: () => void): void;
  fadeOut(done: () => void): void;
  fadeIn(): void;
  /** Snap the cube back to the resting orientation for a fresh run. */
  resetOrientation(): void;
}

/**
 * Which way START turns. `rotationY(-pi/2)` brings the +X face — where the
 * difficulty page is anchored — to the camera: the front face slides out
 * left, the new face arrives from the right, matching the original's
 * `RotationTransition.ClockWise` (Unity `AngleAxis(90, Y)` under its
 * left-handed camera). BACK mirrors it.
 */
export const CLOCKWISE: -1 = -1;
export const COUNTER_CLOCKWISE: 1 = 1;

export class Shell {
  private current: ShellState;
  private session: SessionLike;

  constructor(private readonly deps: ShellDeps) {
    this.current = { kind: "menu", page: "main", pressed: null };
    this.session = deps.buildMenuSession();
  }

  get state(): ShellState {
    return this.current;
  }

  /** Whether swipes may start a cube turn. In menu states they are dropped. */
  get swipesToSim(): boolean {
    return this.current.kind === "running";
  }

  /** Whether taps go to sim picking rather than menu items. */
  get tapsToSim(): boolean {
    return this.current.kind === "running";
  }

  /**
   * The page whose items are tappable right now, or null while rotating,
   * pressed, starting or running — "nothing is tappable while rotating or
   * pressed", and only the active page ever hit-tests.
   */
  get activePage(): MenuPage | null {
    if (this.current.kind !== "menu" || this.current.pressed !== null) return null;
    return this.current.page;
  }

  /** A tap that hit `item` on screen. Ignored unless it is active-page live. */
  tapItem(item: MenuItemName): void {
    if (this.current.kind !== "menu" || this.current.pressed !== null) return;
    if (!PAGE_ITEMS[this.current.page].includes(item)) return;
    this.current = { ...this.current, pressed: { item, elapsed: 0 } };
  }

  /** Advance the press clock; fires the pressed item's action on clip end. */
  update(dt: number): void {
    if (this.current.kind !== "menu" || this.current.pressed === null) return;
    this.current.pressed.elapsed += dt;
    if (this.current.pressed.elapsed < MENU_PRESS_SECONDS) return;
    this.act(this.current.pressed.item);
  }

  private act(item: MenuItemName): void {
    switch (item) {
      case "start":
        this.current = { kind: "rotating", toPage: "difficulty" };
        this.deps.rotate(CLOCKWISE, () => {
          this.current = { kind: "menu", page: "difficulty", pressed: null };
        });
        return;
      case "back":
        this.current = { kind: "rotating", toPage: "main" };
        this.deps.rotate(COUNTER_CLOCKWISE, () => {
          this.current = { kind: "menu", page: "main", pressed: null };
        });
        return;
      case "easy":
      case "normal":
      case "hard": {
        const difficulty = itemDifficulty(item);
        if (difficulty === null) return; // unreachable; narrows the type
        this.current = { kind: "starting", difficulty };
        this.deps.fadeOut(() => {
          // The menu session (and everything it decayed into) dies here; the
          // run is a disposable session object, never module state. Its
          // `gameLost` will genuinely have fired on a long-idled menu — the
          // shell subscribes to nothing, so it cannot leak into the lifecycle.
          this.session.dispose();
          this.deps.resetOrientation();
          this.session = this.deps.buildGameSession(difficulty);
          this.deps.fadeIn();
          this.current = { kind: "running" };
        });
        return;
      }
      case "howto":
      case "options":
      case "highscore":
        // Placeholders: press animation only, back to the page (plan 17 §7).
        this.current = { kind: "menu", page: "main", pressed: null };
        return;
      default: {
        const unreachable: never = item;
        throw new Error(`unhandled menu item ${JSON.stringify(unreachable)}`);
      }
    }
  }
}
